import axios, { InternalAxiosRequestConfig, AxiosRequestHeaders } from 'axios';
import { useSessionExpiredModalStore } from 'src/store/session-expired-modal';

// Utility function to get client validation headers
const getClientValidationHeaders = () => {
  if (typeof window === 'undefined') return {};

  const groupHash =
    import.meta.env.VITE_APP_GROUP_HASH ||
    document
      .querySelector('meta[name="x-group-hash"]')
      ?.getAttribute('content');

  let groupHost = document
    .querySelector('meta[name="x-group-host"]')
    ?.getAttribute('content');
  // For dev environment, use VITE_APP_DOMAIN_DEV if host contains localhost
  if (!groupHost || groupHost.trim() === '') {
    groupHost = import.meta.env.VITE_APP_STAGE;
  }

  if (!groupHost) {
    groupHost = new URL(window.location.href).host;
    if (groupHost.includes('localhost') || groupHost.includes('192.168')) {
      groupHost = import.meta.env.VITE_APP_DOMAIN_DEV || groupHost;
    }
  }

  return {
    ...(groupHash && { 'x-group-hash': groupHash }),
    ...(groupHost && { 'x-group-host': groupHost }),
  };
};

export const api = axios.create({
  baseURL: `${import.meta.env.VITE_APP_BASE_URL as string}api/`,
  paramsSerializer: (
    params: Record<
      string,
      string | number | boolean | (string | number | boolean)[]
    >
  ) => {
    const result = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (Array.isArray(value)) {
        // Serialize arrays without brackets
        value.forEach((item) => result.append(key, String(item)));
      } else {
        result.append(key, String(value));
      }
    }
    return result.toString();
  },
});

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    // Attach JWT token from localStorage if available
    const token =
      typeof window !== 'undefined' ? localStorage.getItem('token') : null;

    // Get client validation headers
    const clientHeaders = getClientValidationHeaders();

    // Initialize headers if undefined to avoid type error
    config.headers = {
      ...config.headers,
      ...clientHeaders,
      ...(token && { Authorization: `Bearer ${token}` }),
    } as AxiosRequestHeaders;

    return config;
  }
);

/**
 * A group-scoped axios instance that does NOT attach the JWT. Some public,
 * pre-auth endpoints (e.g. the W2 `GET /public/link/:token` branding/gate) reject
 * authenticated requests with 403; they must be called with the group-validation
 * headers only. Everything else should use `api`.
 */
export const publicApi = axios.create({ baseURL: api.defaults.baseURL });

publicApi.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    config.headers = {
      ...config.headers,
      ...getClientValidationHeaders(),
    } as AxiosRequestHeaders;
    return config;
  }
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      if (typeof window !== 'undefined') {
        localStorage.removeItem('token');
      }

      const requestHeaders =
        (error.config?.headers as
          | Record<string, string | undefined>
          | undefined) ?? undefined;
      const hasAuthHeader = Boolean(
        requestHeaders?.Authorization || requestHeaders?.authorization
      );

      const publicPaths = [
        '/login',
        '/registration',
        '/forgot-password',
        '/reset-password',
      ];
      const isOnPublicPath =
        typeof window !== 'undefined'
          ? publicPaths.includes(window.location.pathname)
          : false;

      if (hasAuthHeader && !isOnPublicPath) {
        const { openModal } = useSessionExpiredModalStore.getState();
        openModal();
      }
    }
    return Promise.reject(error);
  }
);
